import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Verrou global partagé, résistant au remplacement de <alhel-cart-extras>
 * par un morph (Shopify peut détruire/recréer l'élément, un objet sur
 * `window` survit à ça). Empêche deux requêtes d'ajout/retrait du cadeau de
 * partir en même temps, quelle que soit l'instance qui les déclenche.
 */
function getGiftLock() {
  return (window.__alhelGiftLock ||= { busy: false, pending: false, startedAt: 0 });
}

class AlhelCartExtras extends HTMLElement {
  drawerCleanup = null;

  connectedCallback() {
    document.addEventListener('click', this.onClick);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.installDrawerInteractions();
    this.installCompactAccordions();

    // Vérifie une seule fois, immédiatement, que l'état déjà rendu par
    // Liquid correspond à la réalité du panier. C'est notamment ce qui
    // répare le cas où ce composant vient d'apparaître pour la première
    // fois (panier qui passe de vide à non-vide) : il n'y a alors aucun
    // autre événement à écouter, il faut vérifier tout de suite.
    this.syncGift();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onClick);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.drawerCleanup?.();
  }

  /**
   * Unique point d'entrée de la synchronisation : chaque mise à jour du
   * panier (ajout natif, changement de quantité, remise, notre propre
   * ajout/retrait de cadeau, etc.) passe par cet événement standard du
   * thème Tinker. On ne réagit à rien d'autre (pas de MutationObserver, pas
   * de setInterval, pas de second système en parallèle).
   */
  onCartUpdate = (event) => {
    if (event?.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => this.syncGift()).catch(() => {});
    } else {
      this.syncGift();
    }
  };

  onClick = (event) => {
    const button = event.target.closest('[data-alhel-upsell-add]');
    if (!button || button.disabled) return;
    button.disabled = true;
    const label = button.textContent;
    button.textContent = 'Ajout…';
    this.dispatchCartMutation(
      '/cart/add.js',
      { items: [{ id: Number(button.dataset.alhelUpsellAdd), quantity: 1 }] },
      'add'
    ).catch(() => {
      button.disabled = false;
      button.textContent = label;
    });
  };

  async getCart() {
    const response = await fetch(`/cart.js?_alhel=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Panier indisponible');
    return response.json();
  }

  isGiftLine(item) {
    return item.properties?._alhel_free_gift === 'true';
  }

  async syncGift() {
    if (this.dataset.giftEnabled !== 'true') return;

    const lock = getGiftLock();

    // Sécurité anti-blocage si une requête précédente n'est jamais arrivée
    // jusqu'à son "finally" (onglet fermé pendant le fetch, etc.).
    if (lock.busy && Date.now() - lock.startedAt > 8000) {
      lock.busy = false;
    }

    if (lock.busy) {
      // Une synchronisation est déjà en cours : elle relira l'état frais du
      // panier avant de se terminer, donc pas la peine d'en empiler une
      // deuxième. On note juste qu'il faudra revérifier juste après, au cas
      // où l'événement qui nous a réveillés porte une info plus récente que
      // celle que la requête en cours est en train de traiter.
      lock.pending = true;
      return;
    }

    const variantId = Number(this.dataset.giftVariantId || 0);
    const threshold = Number(this.dataset.threshold || 0);
    if (!variantId || !threshold) return;

    lock.busy = true;
    lock.startedAt = Date.now();

    try {
      const cart = await this.getCart();
      const giftLines = cart.items.filter((item) => this.isGiftLine(item));
      const eligibleTotal = cart.items.reduce(
        (sum, item) => (this.isGiftLine(item) ? sum : sum + Number(item.final_line_price ?? 0)),
        0
      );

      if (giftLines.length > 1) {
        // Répare un panier qui contenait déjà deux lignes cadeau (résidu de
        // l'ancien système à double synchronisation) : on garde la première,
        // on supprime les autres.
        const [, ...extraLines] = giftLines;
        for (const line of extraLines) {
          await this.dispatchCartMutation('/cart/change.js', { id: line.key, quantity: 0 }, 'remove');
        }
        return;
      }

      const giftLine = giftLines[0] || null;

      if (eligibleTotal >= threshold && !giftLine) {
        await this.dispatchCartMutation(
          '/cart/add.js',
          { items: [{ id: variantId, quantity: 1, properties: { _alhel_free_gift: 'true' } }] },
          'add'
        );
      } else if (eligibleTotal < threshold && giftLine) {
        await this.dispatchCartMutation('/cart/change.js', { id: giftLine.key, quantity: 0 }, 'remove');
      } else if (giftLine && giftLine.quantity !== 1) {
        // Le cadeau ne doit jamais exister en quantité > 1.
        await this.dispatchCartMutation('/cart/change.js', { id: giftLine.key, quantity: 1 }, 'update');
      }
    } catch (error) {
      console.warn('[Alhel] Synchronisation du cadeau impossible', error);
    } finally {
      lock.busy = false;
      if (lock.pending) {
        lock.pending = false;
        this.syncGift();
      }
    }
  }

  /**
   * Envoie une mutation panier (/cart/add.js ou /cart/change.js) en passant
   * par le même bus d'événements que le reste du thème Tinker
   * (StandardEvents.cartLinesUpdate + une promesse différée). On ne fait
   * JAMAIS notre propre morphSection ici : c'est `cart-items-component`
   * (assets/component-cart-items.js), qui écoute déjà cet événement sur
   * `document`, qui se charge de morphir `cart-drawer-section` — exactement
   * comme il le fait pour le sélecteur de quantité natif. Avoir deux
   * systèmes qui morphent la même section en parallèle est ce qui
   * provoquait les doublons de cadeau et cassait les boutons "Tu pourrais
   * aussi aimer" dans les tentatives précédentes.
   */
  async dispatchCartMutation(url, payload, action) {
    const sectionId = this.dataset.sections || 'cart-drawer-section';
    const body = JSON.stringify({ ...payload, sections: sectionId, sections_url: window.location.pathname });

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.errors) {
        throw new Error(data.description || data.errors || 'Modification du panier impossible');
      }

      // /cart/add.js et /cart/change.js ne renvoient pas la même forme de
      // réponse (add.js renvoie la/les ligne(s) ajoutée(s), pas le panier
      // complet) : on relit /cart.js pour avoir un instantané fiable et
      // cohérent du panier à mettre dans l'événement.
      const cart = await this.getCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: data.sections || {},
          items: cart.items,
          itemCount: cart.item_count,
          source: 'alhel-cart-extras',
          didError: false,
        },
      });

      return cart;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }

  installCompactAccordions() {
    document.querySelectorAll('#cart-drawer .cart-actions details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        document.querySelectorAll('#cart-drawer .cart-actions details').forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });

    const noteContent = document.querySelector('#cart-drawer .cart-note .details-content');
    if (noteContent && !noteContent.querySelector('[data-alhel-note-valid]')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button alhel-note-valid';
      button.dataset.alhelNoteValid = '';
      button.textContent = 'Valider';
      button.addEventListener('click', () => {
        const details = button.closest('details');
        window.setTimeout(() => {
          if (details) details.open = false;
        }, 260);
      });
      noteContent.appendChild(button);
    }
  }

  installDrawerInteractions() {
    const dialog = document.querySelector('#cart-drawer .theme-drawer__dialog');
    const drawer = document.querySelector('#cart-drawer');
    if (!dialog || !drawer || window.matchMedia('(min-width: 990px)').matches) return;

    let startY = 0;
    let currentY = 0;
    let dragging = false;

    const down = (event) => {
      const rect = dialog.getBoundingClientRect();
      if (event.clientY > rect.top + 42 || event.target.closest('button, a, input, textarea, summary')) return;
      dragging = true;
      startY = event.clientY;
      currentY = startY;
      dialog.setPointerCapture?.(event.pointerId);
      dialog.style.transition = 'none';
    };
    const move = (event) => {
      if (!dragging) return;
      currentY = Math.max(startY, event.clientY);
      dialog.style.transform = `translateY(${currentY - startY}px)`;
    };
    const up = () => {
      if (!dragging) return;
      dragging = false;
      const distance = currentY - startY;
      dialog.style.transition = '';
      dialog.style.transform = '';
      if (distance > 72 && typeof drawer.close === 'function') drawer.close();
    };

    dialog.addEventListener('pointerdown', down);
    dialog.addEventListener('pointermove', move);
    dialog.addEventListener('pointerup', up);
    dialog.addEventListener('pointercancel', up);
    this.drawerCleanup = () => {
      dialog.removeEventListener('pointerdown', down);
      dialog.removeEventListener('pointermove', move);
      dialog.removeEventListener('pointerup', up);
      dialog.removeEventListener('pointercancel', up);
    };
  }
}

if (!customElements.get('alhel-cart-extras')) customElements.define('alhel-cart-extras', AlhelCartExtras);